import { api } from './api';

export interface Product {
  id: string;
  name: string;
  sku: string;
  barcode?: string | null;
  description?: string;
  category: string;
  unit: string;
  price: number;
  costPrice: number;
  unitPrice: number;
  currentStock: number;
  minimumStock: number;
  maximumStock: number;
  location?: string;
  // HSN / SAC code used on GST documents
  hsnCode?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductDto {
  name: string;
  sku: string;
  barcode?: string | null;
  description?: string;
  category: string;
  unit: string;
  price: number;
  costPrice: number;
  unitPrice: number;
  currentStock: number;
  minimumStock: number;
  maximumStock: number;
  location?: string;
  hsnCode?: string | null;
}

export interface UpdateProductDto extends Partial<CreateProductDto> {}

export interface UpdateStockDto {
  stock: number;
}

export interface AddStockDto {
  quantity: number;
  reason?: string;
  unitCost?: number;
  referenceId?: string;
  referenceType?: string;
  notes?: string;
}

export interface AdjustStockDto {
  adjustmentQuantity: number;
  reason: string;
  unitCost?: number;
  notes?: string;
  location?: string;
  referenceNumber?: string;
}

export interface StockMovement {
  id: string;
  productId: string;
  type: 'in' | 'out' | 'adjustment';
  reason: string;
  quantity: number;
  previousStock: number;
  newStock: number;
  unitCost?: number;
  totalValue?: number;
  referenceId?: string;
  referenceType?: string;
  location?: string;
  notes?: string;
  userId?: string;
  createdAt: string;
  product?: Product;
  user?: any;
}

export type StockStatusFilter = 'low' | 'normal' | 'high';

export interface ProductQueryParams {
  page: number;
  limit: number;
  search?: string;
  category?: string;
  status?: StockStatusFilter;
}

export interface PaginatedProducts {
  data: Product[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface InventorySummary {
  totalProducts: number;
  lowStock: number;
  stockValue: number;
  categories: string[];
}

class InventoryService {
  async getProducts(search?: string): Promise<Product[]> {
    const params = search ? { search } : {};
    const response = await api.get<Product[]>('/inventory', { params });
    return response.data;
  }

  async getProductsPage(query: ProductQueryParams): Promise<PaginatedProducts> {
    const params = {
      page: query.page,
      limit: query.limit,
      ...(query.search ? { search: query.search } : {}),
      ...(query.category ? { category: query.category } : {}),
      ...(query.status ? { status: query.status } : {}),
    };
    const response = await api.get<PaginatedProducts>('/inventory/paginated', { params });
    return response.data;
  }

  async getInventorySummary(): Promise<InventorySummary> {
    const response = await api.get<InventorySummary>('/inventory/summary');
    return response.data;
  }

  async getProduct(id: string): Promise<Product> {
    const response = await api.get<Product>(`/inventory/${id}`);
    return response.data;
  }

  async createProduct(data: CreateProductDto): Promise<Product> {
    const response = await api.post<Product>('/inventory', data);
    return response.data;
  }

  async updateProduct(id: string, data: UpdateProductDto): Promise<Product> {
    const response = await api.patch<Product>(`/inventory/${id}`, data);
    return response.data;
  }

  async deleteProduct(id: string): Promise<void> {
    await api.delete(`/inventory/${id}`);
  }

  async updateStock(id: string, stock: number): Promise<Product> {
    const response = await api.patch<Product>(`/inventory/${id}/stock`, { stock });
    return response.data;
  }

  async getLowStockProducts(): Promise<Product[]> {
    const response = await api.get<Product[]>('/inventory/low-stock');
    return response.data;
  }

  async addStock(id: string, data: AddStockDto): Promise<Product> {
    const response = await api.post<Product>(`/inventory/${id}/add-stock`, data);
    return response.data;
  }

  async adjustStock(id: string, data: AdjustStockDto): Promise<Product> {
    const response = await api.post<Product>(`/inventory/${id}/adjust-stock`, data);
    return response.data;
  }

  async getStockMovements(id: string, limit: number = 50): Promise<StockMovement[]> {
    const response = await api.get<StockMovement[]>(`/inventory/${id}/stock-movements`, {
      params: { limit }
    });
    return response.data;
  }

  async getAllStockMovements(filters?: {
    productId?: string;
    type?: string;
    reason?: string;
    dateFrom?: string;
    dateTo?: string;
  }, limit: number = 100): Promise<StockMovement[]> {
    const response = await api.get<StockMovement[]>('/inventory/stock-movements', {
      params: { ...filters, limit }
    });
    return response.data;
  }
}

export const inventoryService = new InventoryService();